import clsx from 'clsx'
import { i18n } from 'i18next'
import moment from 'moment'
import React, { useEffect, useRef, useState } from 'react'
import { I18nextProvider } from 'react-i18next'
import { QueryClient, QueryClientProvider } from 'react-query'
import { ReactQueryDevtools } from 'react-query/devtools'
import { HashRouter } from 'react-router-dom'
import './app.styles.css'
import { AvailyEvent } from '@customTypes/Availy'
import { Config } from '@customTypes/Config'
import AppRouter from './AppRouter'
import useStyles from './app.styles'
import { InitialDataPrefetch } from './InitialDataPrefetch'
import { ChoicesProvider } from './react/context/ChoiceContext'
import { ConfigProvider } from './react/context/ConfigurationContext'
import { StepsProvider } from './react/context/StepsContext'
import { CloseAvailyProvider } from './react/context/CloseAvailyContext'
import { useVisibilityContext } from './react/context/VisibilityContext'
import PosthogProvider from './react/context/PosthogContext/PosthogProvider'
import setupTranslations from './setupTranslations'
import getTrackingParams from './utils/getTrackingParams'
import { postParentMsg } from './utils/postMessage'
import { useHistory } from './react/hooks/useHistory'
import { PractitionersProvider } from './react/context/PractitionersContext'
import { UpsellProvider } from './react/context/UpsellContext'
import { VerifyPhoneProvider } from './react/context/VerifyPhoneContext'
import { ContactDetailsProvider } from './react/context/ContactDetailsContext'
import { AppMode } from '@customTypes/Generic'
import usePatientSurvey from './react/hooks/usePatientSurvey'
import { InitialStateWrapper } from './react/components/InitialStateWrapper/InitialStateWrapper'
import { AppointmentCalendarProvider } from './react/context/AppointmentCalendarContext/AppointmentCalendarContext'
import { PainDetailsProvider } from './react/context/PainDetailsContext'
import createBrandedTheme from './theme/theme'
import { ThemeProvider } from '@mui/material'
import { ThemeProvider as JssThemeProvider } from 'react-jss'

const AVAILY_ID = 'availy'
const reactQueryClient = new QueryClient({ defaultOptions: { queries: { refetchOnWindowFocus: false } } })

const App = ({
  clinicId,
  elementId,
  asModal,
  successUrl,
  embedded,
  mode,
  lang,
  sourceWebsite,
  treatmentBundleTypeReferenceId,
  sourceWebsiteType,
  brandColor,
  defaultToInternalTracking = false,
}: Config) => {
  const trackingParams = getTrackingParams(window.location)
  const nonModalEmbed = !asModal && embedded

  const initialConfig = {
    clinicId,
    elementId,
    asModal,
    successUrl,
    embedded,
    trackingParams,
    mode,
    lang,
    sourceWebsite,
    sourceWebsiteType,
    nonModalEmbed,
    treatmentBundleTypeReferenceId,
    brandColor,
    defaultToInternalTracking,
  }
  const [loadingTranslations, setLoadingTranslations] = useState(true)
  const [availyWrapperVisible, setAvailyWrapperVisible] = useState<boolean>(false)
  const availyWrapper = useRef<HTMLDivElement | null>(null)
  const [i18nInstance, setI18nInstance] = useState<i18n | null>(null)
  const { isVisible, setIsVisible } = useVisibilityContext()
  const [appMode, setAppMode] = useState<AppMode>(initialConfig.mode)
  const { listenForHistory } = useHistory()
  listenForHistory()

  useEffect(() => {
    setIsVisible?.(!asModal)
  }, [setIsVisible, asModal])

  useEffect(() => {
    // Prevents scrolling of the body when the modal is open
    document.documentElement.classList.toggle('availy-modal-open', isVisible)
  }, [isVisible])

  const classes = useStyles()

  useEffect(() => {
    setupTranslations(lang || 'de')
      .then((instance) => {
        setI18nInstance(instance)
        moment.locale(lang === 'de' ? 'de-DE' : 'en-EN')
      })
      .finally(() => {
        setLoadingTranslations(false)
      })
    /**
     * We want to run this only once on mount this is why
     * we do not add dependencies
     */
  }, [lang])

  useEffect(() => {
    if (availyWrapper.current) {
      const observer = new MutationObserver(() => {
        postParentMsg({ type: AvailyEvent.HEIGHT_CHANGED, payload: availyWrapper?.current?.scrollHeight || 700 })
      })

      observer.observe(availyWrapper.current, { childList: true, subtree: true })
    }
  }, [availyWrapperVisible])

  //Opens patient survey after a delay of 1 second
  usePatientSurvey({
    setAppMode,
    initialConfig,
  })

  const handleAvailyWrapperLoad = (element: HTMLDivElement) => {
    availyWrapper.current = element

    setAvailyWrapperVisible(true)
  }

  const asModalEmbedded = asModal && embedded

  if (loadingTranslations || !i18nInstance) return <div />

  const theme = createBrandedTheme(brandColor)

  return (
    <div
      id={AVAILY_ID}
      aria-modal={isVisible}
      aria-hidden={!isVisible}
      className={clsx(asModal && classes.asModal)}
      style={{ display: isVisible ? 'block' : 'none' }}
    >
      {asModal && <div className={clsx(classes.backdrop, isVisible && classes.modalVisible)} />}
      <JssThemeProvider theme={theme}>
        <ThemeProvider theme={theme}>
          <QueryClientProvider client={reactQueryClient}>
            {process.env.NODE_ENV === 'development' && <ReactQueryDevtools />}
            <ConfigProvider initialConfig={initialConfig}>
              <div
                id="availyWrapper"
                ref={handleAvailyWrapperLoad}
                className={clsx(classes.appContainer, {
                  [classes.nonModalEmbed]: nonModalEmbed,
                  [classes.asModalEmbedded]: asModalEmbedded,
                })}
              >
                <PosthogProvider>
                  <ChoicesProvider>
                    <I18nextProvider i18n={i18nInstance}>
                      <UpsellProvider>
                        <PainDetailsProvider>
                          <InitialStateWrapper appMode={appMode} asModal={!!asModal} isVisible={!!isVisible}>
                            {isVisible && (
                              <HashRouter>
                                <PractitionersProvider>
                                  <StepsProvider>
                                    <VerifyPhoneProvider>
                                      <CloseAvailyProvider onClose={() => setIsVisible?.(false)} mode={appMode}>
                                        <ContactDetailsProvider>
                                          <AppointmentCalendarProvider>
                                            <>
                                              <InitialDataPrefetch />
                                              <AppRouter mode={appMode} />
                                            </>
                                          </AppointmentCalendarProvider>
                                        </ContactDetailsProvider>
                                      </CloseAvailyProvider>
                                    </VerifyPhoneProvider>
                                  </StepsProvider>
                                </PractitionersProvider>
                              </HashRouter>
                            )}
                          </InitialStateWrapper>
                        </PainDetailsProvider>
                      </UpsellProvider>
                    </I18nextProvider>
                  </ChoicesProvider>
                </PosthogProvider>
              </div>
            </ConfigProvider>
          </QueryClientProvider>
        </ThemeProvider>
      </JssThemeProvider>
    </div>
  )
}

export default App
